import { Injectable } from "@angular/core";
import { Router, NavigationEnd } from "@angular/router";
import { ReplaySubject } from "rxjs";
import { PermissionService } from "../permission.service";
import {
  FEATURE_AURA_KEY,
  FEATURE_EXPLORIST_KEY,
  FEATURE_LUMA_KEY,
  FEATURE_TEMPO_KEY,
  FEATURE_TRIPIST_KEY,
} from "../features.constants";
import { SessionService } from "../session.service";
import { UiService } from "../ui/ui.service";
import { IntegrationsService } from "../integrations/integrations.service";
import { createRoute, Route } from "./Route";
import { find } from "lodash";
import { take, filter } from "rxjs/operators";

@Injectable({
  providedIn: "root",
})
export class SidebarService {
  private navigationRoutes!: Route[];
  onNavigationRoutesLoaded = new ReplaySubject<Route[]>(1);
  constructor(
    private router: Router,
    private permissionService: PermissionService,
    private sessionService: SessionService,
    private uiService: UiService,
    private integrationsService: IntegrationsService,
  ) {
    this.permissionService.permissionsLoadedObs.subscribe((res) => {
      if (res) {
        this.initRoutes();
      }
    });

    // Subscribe to account changes to clear cache and re-initialize routes
    this.sessionService.currentAccount.subscribe((account) => {
      if (account) {
        // Clear cached integrations when account changes
        this.cachedIntegrations = [];
        // Re-initialize routes with new account's integrations
        if (this.permissionService.permissionsLoaded) {
          this.initRoutes();
        }
      }
    });

    // Subscribe to app changes to clear cache and re-initialize routes
    // Integrations and certain feature-dependent routes are app-specific
    this.sessionService.currentApp.subscribe((app) => {
      if (app) {
        // Clear cached integrations when app changes
        this.cachedIntegrations = [];
        // Re-initialize routes with new app's context
        if (this.permissionService.permissionsLoaded) {
          this.initRoutes();
        }
      }
    });

    // Subscribe to router events to track current route and maintain sidebar state
    this.router.events
      .pipe(filter((event): event is NavigationEnd => event instanceof NavigationEnd))
      .subscribe((event: NavigationEnd) => {
        this.updateSidebarBasedOnRoute(event.url);
      });
  }

    private cachedIntegrations: any[] = [];
  private pendingSidebarOverride: { code: string; url: string } | null = null;

  private async getIntegrationsData(): Promise<any[]> {
    if (this.cachedIntegrations.length > 0) {
      return this.cachedIntegrations;
    }

    // Wait for session to be ready
    let retries = 0;
    const maxRetries = 10;
    
    while (retries < maxRetries) {
      const accountId = this.sessionService.currentAccountValue?.accountID;
      const appId = this.sessionService.currentAppValue?.id;
      
      if (accountId && appId) {
        break;
      }
      await new Promise(resolve => setTimeout(resolve, 100));
      retries++;
    }
    
    const accountId = this.sessionService.currentAccountValue?.accountID;
    const appId = this.sessionService.currentAppValue?.id;
    
    // Don't make API call if we don't have valid session data
    if (!accountId || !appId) {
      console.warn(`Missing session data for integration lookup after retries (accountId: ${accountId}, appId: ${appId})`);
      return [];
    }
    
    try {
      const response: any = await this.integrationsService.getIntegrations(
        accountId,
        appId,
        true
      ).pipe(take(1)).toPromise();
      
      // Handle both array response and object with records property
      this.cachedIntegrations = Array.isArray(response) ? response : response?.records || [];
      return this.cachedIntegrations;
    } catch (error) {
      console.error(`Error fetching integrations:`, error);
      return [];
    }
  }

  private findIntegrationIdByType(allIntegrations: any[], type: string): number | null {
    if (allIntegrations.length === 0) {
      return null;
    }
    
    const filteredIntegrations = allIntegrations.filter((integration: any) => {
      return integration.provider?.type === type;
    });
    
    if (filteredIntegrations && filteredIntegrations.length > 0) {
      return filteredIntegrations[0].id;
    }
    
    return null;
  }

  private async getIntegrationId(type: string): Promise<number | null> {
    const allIntegrations = await this.getIntegrationsData();
    return this.findIntegrationIdByType(allIntegrations, type);
  }

  private async getTripistIntegrationId(): Promise<number | null> {
    return this.getIntegrationId("tripist_assistant");
  }

  private async getExploristIntegrationId(): Promise<number | null> {
    return this.getIntegrationId("explorist_assistant");
  }

  private async getAuraIntegrationId(): Promise<number | null> {
    return this.getIntegrationId("aura_assistant");
  }

  private updateSidebarBasedOnRoute(url: string): void {
    if (!this.navigationRoutes) {
      return;
    }

    // Check if the URL contains assistist with feature type
    const assististMatch = url.match(/\/assistist\/([^\/]+)/);
    if (assististMatch) {
      const featureType = assististMatch[1];
      
      // Determine which sidebar section should be expanded
      let targetCode: string | null = null;
      if (featureType === 'tripist') {
        targetCode = 'tripist';
      } else if (featureType === 'aura') {
        targetCode = 'aura';
      } else if (featureType === 'explorist') {
        targetCode = 'explorist';
      }

      if (targetCode) {
        // Close all currently opened routes
        this.closeAllRoutes(this.navigationRoutes);

        // Find the target route at any level
        const targetRoute = this.findRouteByCode(this.navigationRoutes, targetCode);
        if (targetRoute) {
          // Find and open the entire path to the target route
          const routePath = this.findRoutePathToTarget(this.navigationRoutes, targetRoute);
          if (routePath) {
            this.openRoutePath(routePath);
          }
        }
      }
    }
  }

  // Build reusable sections (fresh instances each call)
  private buildFlowsRoute(): Promise<Route> {
    return createRoute({
      name: "Flows",
      icon: "flows",
      code: "flows",
      disabled: false,
      showFunction: () => {
        return this.permissionService.hasFeature(
          FEATURE_TEMPO_KEY,
          this.sessionService.currentAccountValue,
        );
      },
      subRoutes: [
        createRoute({
          name: "Create New Flow",
          path: ["flows", "create", "app"],
          showFunction: () => {
            return this.permissionService.categories.logicTrees.hasWrite();
          },
          icon: "pen-plus",
        }),
        createRoute({
          name: "Flow Library",
          path: ["flows", "library", "app"],
          showFunction: () => {
            return this.permissionService.categories.logicTrees.hasList();
          },
          icon: "3-vert-bars",
        }),
        createRoute({
          name: "Internal Flows",
          icon: "3-vert-bars",
          showFunction: () => {
            return this.sessionService.isSuper;
          },
          path: ["flows", "library", "internal"],
        }),
        createRoute({
          name: "Testing",
          code: "flow-tests",
          path: ["flow-tests", "list"],
          icon: "3-vert-bars",
          showFunction: () => {
            return this.permissionService.categories.logicTrees.hasList();
          },
        }),
      ],
    });
  }

  private buildBroadcastsRoute(): Promise<Route> {
    return createRoute({
      name: "Broadcasts",
      icon: "broadcast",
      code: "broadcasts",
      showFunction: () => {
        return this.permissionService.hasFeature(
          FEATURE_TEMPO_KEY,
          this.sessionService.currentAccountValue,
        );
      },
      subRoutes: [
        createRoute({
          name: "Broadcast List",
          path: ["broadcasts", "list"],
          showFunction: () => {
            return this.permissionService.categories.broadcasts.hasList();
          },
          icon: "clipboard-pencil",
          iconStyle: {
            "width.px": "23.055",
            "height.px": "24.99",
          },
        }),
        createRoute({
          name: "Create Broadcast",
          path: ["broadcasts", "create"],
          showFunction: () => {
            return Promise.all([
              this.permissionService.categories.broadcasts.hasList(),
              this.permissionService.categories.broadcasts.hasWrite(),
              this.permissionService.categories.accountSignalCategories.hasList(),
              this.permissionService.categories.templates.hasList(),
            ]).then(([bcHasList, bcHasWrite, categoriesHasList, templatesHasList]) => {
              return bcHasList && bcHasWrite && categoriesHasList && templatesHasList;
            });
          },
          icon: "lightning-bolt",
          iconStyle: {
            "width.px": "23.055",
            "height.px": "24.99",
          },
        }),
        createRoute({
          name: "Define New Audience",
          path: ["broadcasts", "audiences", "create"],
          showFunction: () => {
            return this.permissionService.categories.broadcasts.hasWrite();
          },
          icon: "audience",
          iconStyle: {
            "width.px": "22.518",
            "height.px": "22.822",
          },
        }),
        createRoute({
          name: "Saved Audiences",
          path: ["broadcasts", "audiences", "list"],
          showFunction: () => {
            return this.permissionService.categories.broadcasts.hasList();
          },
          icon: "saved-audience",
          iconStyle: {
            "width.px": "21.189",
            "height.px": "21.128",
          },
        }),
        createRoute({
          name: "Broadcast Performance",
          path: ["broadcasts", "performance"],
          showFunction: () => {
            return this.permissionService.categories.broadcasts.hasList();
          },
          icon: "graph-performance",
          iconStyle: {
            "width.px": "22.051",
            "height.px": "19.37",
          },
        }),
      ],
    });
  }

  private buildContentRoute(): Promise<Route> {
    return createRoute({
      name: "Content",
      icon: "content",
      code: "content",
      showFunction: () => {
        const hasTempo = this.permissionService.hasFeature(
          FEATURE_TEMPO_KEY,
          this.sessionService.currentAccountValue,
        );
        const hasTripist = this.permissionService.hasFeature(
          FEATURE_TRIPIST_KEY,
          this.sessionService.currentAccountValue,
        );
        const hasExplorist = this.permissionService.hasFeature(
          FEATURE_EXPLORIST_KEY,
          this.sessionService.currentAccountValue,
        );
        const hasAura = this.permissionService.hasFeature(
          FEATURE_AURA_KEY,
          this.sessionService.currentAccountValue,
        );
        return hasTempo || hasTripist || hasExplorist || hasAura;
      },
      subRoutes: [
        createRoute({
          name: "Template Library",
          path: ["content", "template", "library"],
          icon: "template",
          showFunction: async () => {
            const hasTemplatesPerm = await this.permissionService.categories.templates.hasList();
            const hasTempoFeature = this.permissionService.hasFeature(
              FEATURE_TEMPO_KEY,
              this.sessionService.currentAccountValue,
            );
            return hasTemplatesPerm && hasTempoFeature;
          },
        }),
        createRoute({
          name: "Media Library",
          path: ["content", "media", "library"],
          icon: "media",
          showFunction: () => {
            const hasMediaPerm = this.permissionService.categories.media.hasList();
            const hasTripistFeature = this.permissionService.hasFeature(
              FEATURE_TRIPIST_KEY,
              this.sessionService.currentAccountValue,
            );
            const hasExploristFeature = this.permissionService.hasFeature(
              FEATURE_EXPLORIST_KEY,
              this.sessionService.currentAccountValue,
            );
            const hasAuraFeature = this.permissionService.hasFeature(
              FEATURE_AURA_KEY,
              this.sessionService.currentAccountValue,
            );
            return hasMediaPerm || hasTripistFeature || hasExploristFeature || hasAuraFeature;
          },
        }),
      ],
    });
  }

  private buildCustomersRoute(): Promise<Route> {
    return createRoute({
      name: "Customers",
      icon: "customers",
      code: "customers",
      showFunction: () => {
        return (
          this.permissionService.hasFeature(
            FEATURE_TEMPO_KEY,
            this.sessionService.currentAccountValue,
          ) ||
          this.permissionService.hasFeature(
            FEATURE_TRIPIST_KEY,
            this.sessionService.currentAccountValue,
          ) ||
          this.permissionService.hasFeature(
            FEATURE_EXPLORIST_KEY,
            this.sessionService.currentAccountValue,
          ) ||
          this.permissionService.hasFeature(
            FEATURE_AURA_KEY,
            this.sessionService.currentAccountValue,
          ) ||
          this.permissionService.hasFeature(
            FEATURE_LUMA_KEY,
            this.sessionService.currentAccountValue,
          )
        );
      },
      subRoutes: [
        createRoute({
          name: "Master Roster",
          path: ["customers", "roster"],
          icon: "clipboard-pencil",
          iconStyle: {
            "width.px": "22.129",
            "height.px": "22.019",
          },
          showFunction: () => {
            return Promise.all([
              this.permissionService.categories.accountAppUsers.hasList(),
            ]).then(([aauList]) => {
              return aauList;
            });
          },
        }),
        createRoute({
          name: "Saved Customer Lists",
          path: ["customers", "customerLists"],
          icon: "customer-list",
          iconStyle: {
            "width.px": "21.189",
            "height.px": "21.189",
          },
          showFunction: () => {
            return this.permissionService.categories.broadcasts.hasList();
          },
        }),
        createRoute({
          name: "Imports",
          path: ["customers", "imports", "list"],
          icon: "list",
          iconStyle: {
            "width.px": "20",
            "height.px": "20",
          },
          showFunction: () => {
            return this.permissionService.categories.accountAppUsers.hasList();
          },
        }),
        createRoute({
          name: "Customer Insights",
          path: ["customers", "customerInsights"],
          icon: "lightbulb-bright",
          iconStyle: {
            "width.px": "21.5",
            "height.px": "21.5",
          },
          showFunction: () => {
            return Promise.all([
              this.permissionService.categories.accountAppUsers.hasList(),
              Promise.resolve(
                this.permissionService.hasFeature(
                  FEATURE_TEMPO_KEY,
                  this.sessionService.currentAccountValue,
                ),
              ),
            ]).then(([hasList, hasFeature]) => hasList && hasFeature);
          },
        }),
        createRoute({
          name: "Create Broadcast",
          path: ["customers", "createBroadcast"],
          showFunction: () => {
            return Promise.all([
              this.permissionService.categories.broadcasts.hasList(),
              this.permissionService.categories.broadcasts.hasWrite(),
              this.permissionService.categories.accountSignalCategories.hasList(),
              this.permissionService.categories.templates.hasList(),
              Promise.resolve(
                this.permissionService.hasFeature(
                  FEATURE_TEMPO_KEY,
                  this.sessionService.currentAccountValue,
                ),
              ),
            ]).then(
              ([
                bcHasList,
                bcHasWrite,
                categoriesHasList,
                templatesHasList,
                hasFeature,
              ]) => {
                return (
                  bcHasList &&
                  bcHasWrite &&
                  categoriesHasList &&
                  templatesHasList &&
                  hasFeature
                );
              },
            );
          },
          icon: "lightning-bolt",
        }),
      ],
    });
  }

  private buildInfrastructureRoute(): Promise<Route> {
    return createRoute({
      name: "Infrastructure",
      icon: "infrastructure",
      code: "infrastructure",
      showFunction: () => {
        return this.permissionService.hasFeature(
          FEATURE_TEMPO_KEY,
          this.sessionService.currentAccountValue,
        );
      },
      subRoutes: [
        createRoute({
          name: "Facilities",
          path: ["facilities", "list"],
          icon: "facilities",
          iconStyle: {
            "width.px": "24.081",
            "height.px": "26.5",
          },
        }),
        createRoute({
          name: "Geofences",
          path: ["geofences", "list"],
          icon: "geofences",
        }),
        createRoute({
          name: "Beacons",
          path: ["beacons", "list"],
          icon: "beacon",
        }),
        createRoute({
          name: "Beacon Regions",
          path: ["beacon-regions", "list"],
          icon: "regions",
        }),
      ],
    });
  }

  async initRoutes() {
    const dashboardRoutesPromise = createRoute({
      name: "Dashboard",
      code: "dashboard",
      icon: "dashboard",
      path: ["dashboard"],
    });

    const flowsRoutesPromise = this.buildFlowsRoute();
    const broadcastsRoutesPromise = this.buildBroadcastsRoute();
    const contentRoutesPromise = this.buildContentRoute();
    const lumaRoutesPromise = createRoute({
      name: "Luma",
      icon: "luma",
      code: "luma",
      path: ["luma"],
      showFunction: () => {
        return this.permissionService.hasFeature(
          FEATURE_LUMA_KEY,
          this.sessionService.currentAccountValue,
        );
      },
      subRoutes: [
        createRoute({
          name: "Campaigns",
          path: ["luma", "campaigns"],
          icon: "clipboard-pencil",
          iconStyle: {
            "width.px": "22.129",
            "height.px": "22.019",
          },
        }),
        createRoute({
          name: "Create Campaign",
          path: ["luma", "create"],
          icon: "lightning-bolt",
          iconStyle: {
            "width.px": "22.129",
            "height.px": "22.019",
          },
        }),
        createRoute({
          name: "Media Library",
          path: ["content", "media", "library"],
          icon: "media",
          showFunction: () => {
            return this.permissionService.categories.media.hasList();
          },
          sidebarOverride: "luma",
        }),
      ],
    });

    const customerRoutesPromise = this.buildCustomersRoute();

    // Get integrations once and use for both lookups
    const allIntegrations = await this.getIntegrationsData();
    
    const tripistIntegrationId = this.findIntegrationIdByType(allIntegrations, "tripist_assistant");
    const exploristIntegrationId = this.findIntegrationIdByType(allIntegrations, "explorist_assistant");
    const auraIntegrationId = this.findIntegrationIdByType(allIntegrations, "aura_assistant");

    const tripistRoutesPromise = createRoute({
      name: "Tripist",
      icon: "tripist",
      code: "tripist",
      iconStyle: {
        "width.px": "30",
        "height.px": "30",
      },
      showFunction: () => {
        return !!tripistIntegrationId && this.permissionService.hasFeature(
          FEATURE_TRIPIST_KEY,
          this.sessionService.currentAccountValue,
        );
      },
      subRoutes: [
        createRoute({
          name: "Categories",
          path: ["assistist", "tripist", tripistIntegrationId?.toString() || "0", "categories", "list"],
          icon: "assistist-categories",
          iconStyle: {
            "width.px": "20",
            "height.px": "20",
          },
        }),
        createRoute({
          name: "Places",
          path: ["assistist", "tripist", tripistIntegrationId?.toString() || "0", "documents", "list"],
          icon: "assistist-places",
          iconStyle: {
            "width.px": "20",
            "height.px": "20",
          },
        }),
        createRoute({
          name: "Preview & Test",
          path: ["assistist", "tripist", tripistIntegrationId?.toString() || "0", "preview"],
          icon: "assistist-preview",
          iconStyle: {
            "width.px": "20",
            "height.px": "20",
          },
        }),
        createRoute({
          name: "Usage",
          path: ["assistist", "tripist", tripistIntegrationId?.toString() || "0", "usage-report"],
          icon: "assistist-usage",
          iconStyle: {
            "width.px": "20",
            "height.px": "20",
          },
        }),
        createRoute({
          name: "Threads",
          path: ["assistist", "tripist", tripistIntegrationId?.toString() || "0", "threads", "list"],
          icon: "assistist-threads",
          iconStyle: {
            "width.px": "20",
            "height.px": "20",
          },
        }),
        createRoute({
          name: "Feedback",
          path: ["assistist", "tripist", tripistIntegrationId?.toString() || "0", "feedback", "list"],
          icon: "assistist-feedback",
          iconStyle: {
            "width.px": "20",
            "height.px": "20",
          },
        }),
        createRoute({
          name: "Media Library",
          path: ["content", "media", "library"],
          icon: "media",
          showFunction: () => {
            return this.permissionService.categories.media.hasList();
          },
          sidebarOverride: "tripist",
        }),
      ]
    });

    const exploristRoutesPromise = createRoute({
      name: "Explorist",
      icon: "explorist",
      code: "explorist",
      iconStyle: {
        "width.px": "30",
        "height.px": "30",
      },
      showFunction: () => {
        return !!exploristIntegrationId && this.permissionService.hasFeature(
          FEATURE_EXPLORIST_KEY,
          this.sessionService.currentAccountValue,
        );
      },
      subRoutes: [
        createRoute({
          name: "Categories",
          path: ["assistist", "explorist", exploristIntegrationId?.toString() || "0", "categories", "list"],
          icon: "assistist-categories",
          iconStyle: {
            "width.px": "20",
            "height.px": "20",
          },
        }),
        createRoute({
          name: "Products",
          path: ["assistist", "explorist", exploristIntegrationId?.toString() || "0", "documents", "list"],
          icon: "assistist-places",
          iconStyle: {
            "width.px": "20",
            "height.px": "20",
          },
        }),
        createRoute({
          name: "Preview & Test",
          path: ["assistist", "explorist", exploristIntegrationId?.toString() || "0", "preview"],
          icon: "assistist-preview",
          iconStyle: {
            "width.px": "20",
            "height.px": "20",
          },
        }),
        createRoute({
          name: "Usage",
          path: ["assistist", "explorist", exploristIntegrationId?.toString() || "0", "usage-report"],
          icon: "assistist-usage",
          iconStyle: {
            "width.px": "20",
            "height.px": "20",
          },
        }),
        createRoute({
          name: "Threads",
          path: ["assistist", "explorist", exploristIntegrationId?.toString() || "0", "threads", "list"],
          icon: "assistist-threads",
          iconStyle: {
            "width.px": "20",
            "height.px": "20",
          },
        }),
        createRoute({
          name: "Feedback",
          path: ["assistist", "explorist", exploristIntegrationId?.toString() || "0", "feedback", "list"],
          icon: "assistist-feedback",
          iconStyle: {
            "width.px": "20",
            "height.px": "20",
          },
        }),
        createRoute({
          name: "Media Library",
          path: ["content", "media", "library"],
          icon: "media",
          showFunction: () => {
            return this.permissionService.categories.media.hasList();
          },
          sidebarOverride: "explorist",
        }),
      ]
    });

    const auraRoutesPromise = createRoute({
      name: "Aura",
      icon: "aura",
      code: "aura",
      iconStyle: {
        "width.px": "30",
        "height.px": "30",
      },
      showFunction: () => {
        return !!auraIntegrationId && this.permissionService.hasFeature(
          FEATURE_AURA_KEY,
          this.sessionService.currentAccountValue,
        );
      },
      subRoutes: [
        createRoute({
          name: "Categories",
          path: ["assistist", "aura", auraIntegrationId?.toString() || "0", "categories", "list"],
          icon: "assistist-categories",
          iconStyle: {
            "width.px": "20",
            "height.px": "20",
          },
        }),
        createRoute({
          name: "Inventory",
          path: ["assistist", "aura", auraIntegrationId?.toString() || "0", "documents", "list"],
          icon: "assistist-inventory",
          iconStyle: {
            "width.px": "20",
            "height.px": "20",
          },
        }),
        createRoute({
          name: "Preview & Test",
          path: ["assistist", "aura", auraIntegrationId?.toString() || "0", "preview"],
          icon: "assistist-preview",
          iconStyle: {
            "width.px": "20",
            "height.px": "20",
          },
        }),
        createRoute({
          name: "Usage",
          path: ["assistist", "aura", auraIntegrationId?.toString() || "0", "usage-report"],
          icon: "assistist-usage",
          iconStyle: {
            "width.px": "20",
            "height.px": "20",
          },
        }),
        createRoute({
          name: "Threads",
          path: ["assistist", "aura", auraIntegrationId?.toString() || "0", "threads", "list"],
          icon: "assistist-threads",
          iconStyle: {
            "width.px": "20",
            "height.px": "20",
          },
        }),
        createRoute({
          name: "Feedback",
          path: ["assistist", "aura", auraIntegrationId?.toString() || "0", "feedback", "list"],
          icon: "assistist-feedback",
          iconStyle: {
            "width.px": "20",
            "height.px": "20",
          },
        }),
        createRoute({
          name: "Media Library",
          path: ["content", "media", "library"],
          icon: "media",
          showFunction: () => {
            return this.permissionService.categories.media.hasList();
          },
          sidebarOverride: "aura",
        }),
      ]
    });

    const intelligenceRoutesPromise = createRoute({
      name: "Intelligence",
      icon: "intelligence",
      code: "intelligence",
      disabled: false,
      showFunction: () => {
        return this.permissionService.hasFeature(
          FEATURE_TEMPO_KEY,
          this.sessionService.currentAccountValue,
        );
      },
      subRoutes: [
        createRoute({
          name: "Executive Summary",
          path: ["intelligence", "executive-summary"],
          icon: "execSummaryLogo",
          iconStyle: {
            "width.px": "25",
            "height.px": "25",
          },
        }),
        createRoute({
          name: "Insights",
          path: ["intelligence", "insights"],
          icon: "insights",
        }),
        createRoute({
          name: "Redemptions",
          path: ["intelligence", "redemptions"],
          icon: "redemptions",
        }),
        createRoute({
          name: "Device info",
          path: ["intelligence", "deviceInfo"],
          icon: "device",
        }),
        createRoute({
          name: "Regions",
          path: ["intelligence", "regions"],
          icon: "regions",
        }),
        createRoute({
          name: "Heat Map",
          path: ["intelligence", "heatMap"],
          icon: "heatMap",
        }),
      ],
    });

    const infrastructureRoutesPromise = this.buildInfrastructureRoute();

    const adminRoutesPromise = createRoute({
      name: "Admin",
      icon: "admin",
      code: "admin",
      iconStyle: {
        "width.px": "26",
        "height.px": "26",
      },
      showFunction: () => {
        return this.permissionService.hasRole("account_admin");
      },
      subRoutes: [
        createRoute({
          name: "Accounts",
          path: ["accounts", "list"],
          icon: "accounts",
          iconStyle: {
            "width.px": "22.5",
            "height.px": "26.406",
          },
        }),
        createRoute({
          name: "Apps",
          path: ["apps", "list"],
          icon: "phone",
          iconStyle: {
            "width.px": "22.051",
            "height.px": "21.5",
          },
        }),
        createRoute({
          name: "Users",
          path: ["users", "list"],
          icon: "person-circle",

          iconStyle: {
            "width.px": "21.5",
            "height.px": "21.5",
          },
        }),
        createRoute({
          name: "User Groups",
          path: ["users", "groups", "list"],
          icon: "people-group",
          iconStyle: {
            "width.px": "21.398",
            "height.px": "21.398",
          },
        }),
        createRoute({
          name: "Roles & Permissions",
          path: ["roles", "list"],
          icon: "lock",
          iconStyle: {
            "width.px": "18.484",
            "height.px": "24.479",
          },
          showFunction: () => {
            return this.permissionService.categories.roles.hasList();
          },
        }),
        createRoute({
          name: "Instrumentation",
          code: "instrumentation",
          icon: "instrumentation",
          showFunction: () => {
            return this.sessionService.isSuper;
          },
          path: ["instrumentation", "accountsSelection"],
        }),
        createRoute({
          name: "Key Metrics",
          code: "key_metrics",
          icon: "key_metrics",
          showFunction: () => {
            return this.sessionService.isSuper;
          },
          path: ["stats", "key-metrics"],
        }),
      ],
    });

    const integrationsRoutesPromise = createRoute({
      name: "Integrations",
      icon: "integrations",
      code: "integrations",
      showFunction: () => {
        return this.sessionService.isSuper;
      },
      subRoutes: [
        createRoute({
          name: "Create New Integration",
          path: ["integrations", "create"],
          showFunction: () => {
            return this.permissionService.categories.integrations.hasWrite();
          },
          icon: "pen-plus",
        }),
        createRoute({
          name: "Integrations",
          path: ["integrations", "list"],
          showFunction: () => {
            return this.permissionService.categories.integrations.hasList();
          },
          icon: "3-vert-bars",
        }),
      ]
    });


    // Tempo top-level, reusing core sections
    const tempoRoutesPromise = createRoute({
      name: "Tempo",
      icon: "tempo",
      code: "tempo",
      showFunction: () => {
        return this.permissionService.hasFeature(
          FEATURE_TEMPO_KEY,
          this.sessionService.currentAccountValue,
        );
      },
      subRoutes: [
        this.buildFlowsRoute(),
        this.buildBroadcastsRoute(),
        this.buildContentRoute(),
        this.buildInfrastructureRoute(),
      ],
    });

    this.uiService
      .waitForEvent(
        Promise.all([
          dashboardRoutesPromise,
          tempoRoutesPromise,
          tripistRoutesPromise,
          exploristRoutesPromise,
          auraRoutesPromise,
          lumaRoutesPromise,
          customerRoutesPromise,
          intelligenceRoutesPromise,
          integrationsRoutesPromise,
          adminRoutesPromise,
        ]),
      )
      .subscribe((res) => {
        this.navigationRoutes = res;
        this.onNavigationRoutesLoaded.next(this.navigationRoutes);
      });
  }

  get openedRoute() {
    return this.navigationRoutes?.find((route: Route) => {
      return route.opened;
    });
  }

  /**
   * Recursively finds a route by code at any depth in the route hierarchy
   */
  private findRouteByCode(routes: Route[], code: string): Route | null {
    for (const route of routes) {
      if (route.code === code) {
        return route;
      }
      if (route.subRoutes) {
        const found = this.findRouteByCode(route.subRoutes, code);
        if (found) {
          return found;
        }
      }
    }
    return null;
  }

  /**
   * Recursively finds a route by matching its path at any depth
   */
  private findRouteByPath(routes: Route[], targetPath: string[]): Route | null {
    for (const route of routes) {
      if (route.path && this.pathsMatch(route.path, targetPath)) {
        return route;
      }
      if (route.subRoutes) {
        const found = this.findRouteByPath(route.subRoutes, targetPath);
        if (found) {
          return found;
        }
      }
    }
    return null;
  }

  /**
   * Checks if two path arrays match
   */
  private pathsMatch(path1: string[], path2: string[]): boolean {
    if (path1.length !== path2.length) {
      return false;
    }
    for (let i = 0; i < path1.length; i++) {
      if (path1[i] !== path2[i]) {
        return false;
      }
    }
    return true;
  }

  /**
   * Finds the path of parent routes from root to the target route
   */
  private findRoutePathToTarget(routes: Route[], target: Route, path: Route[] = []): Route[] | null {
    for (const route of routes) {
      const currentPath = [...path, route];
      
      if (route === target) {
        return currentPath;
      }
      
      if (route.subRoutes) {
        const found = this.findRoutePathToTarget(route.subRoutes, target, currentPath);
        if (found) {
          return found;
        }
      }
    }
    return null;
  }

  /**
   * Closes all routes recursively
   */
  private closeAllRoutes(routes: Route[]): void {
    for (const route of routes) {
      route.opened = false;
      if (route.subRoutes) {
        this.closeAllRoutes(route.subRoutes);
      }
    }
  }

  /**
   * Opens all routes in the given path array
   */
  private openRoutePath(routePath: Route[]): void {
    for (const route of routePath) {
      if (route.subRoutes && route.subRoutes.length > 0) {
        route.opened = true;
      }
    }
  }

  setNavigationOpenOnChange(sidebarCode?: string) {
    if (!this.navigationRoutes) {
      return;
    }

    let effectiveSidebarCode = sidebarCode;

    if (this.pendingSidebarOverride) {
      const { code, url } = this.pendingSidebarOverride;
      const normalizedCurrentUrl = this.router.url.split('?')[0];
      const normalizedTargetUrl = url.split('?')[0];

      if (normalizedCurrentUrl.startsWith(normalizedTargetUrl)) {
        effectiveSidebarCode = code;
        this.pendingSidebarOverride = null;
      }
    }

    // Handle assistist routes specially - check current URL for feature type
    if (effectiveSidebarCode === 'assistist') {
      const currentUrl = this.router.url;
      this.updateSidebarBasedOnRoute(currentUrl);
      return;
    }

    // Close all currently opened routes
    this.closeAllRoutes(this.navigationRoutes);
    
    // First, try to expand based on the current URL (most accurate)
    const currentUrl = this.router.url;
    const urlSegments = currentUrl.split('/').filter(segment => segment.length > 0);
    let matchingRoute = this.findMatchingRouteByUrl(this.navigationRoutes, urlSegments, effectiveSidebarCode || undefined);
    
    if (matchingRoute) {
      // We found a route that matches the current URL
      const routePath = this.findRoutePathToTarget(this.navigationRoutes, matchingRoute);
      if (routePath) {
        this.openRoutePath(routePath);
        return;
      }
    }
    
    // If URL matching didn't work, try to find by sidebar code
    if (effectiveSidebarCode) {
      // Find all routes with this code (there might be duplicates at different levels)
      const candidateRoutes = this.findAllRoutesByCode(this.navigationRoutes, effectiveSidebarCode);
      
      if (candidateRoutes.length > 0) {
        // If there are multiple candidates, prefer the one whose ancestors match the URL best
        let bestRoute = candidateRoutes[0];
        
        if (candidateRoutes.length > 1) {
          bestRoute = this.findBestMatchingRoute(candidateRoutes, urlSegments, effectiveSidebarCode) || candidateRoutes[0];
        }
        
        // Find the path from root to target
        const routePath = this.findRoutePathToTarget(this.navigationRoutes, bestRoute);
        
        if (routePath) {
          // Open all parent routes in the path
          this.openRoutePath(routePath);
        }
      }
    }
  }

  /**
   * Finds all routes with a specific code at any depth
   */
  private findAllRoutesByCode(routes: Route[], code: string, results: Route[] = []): Route[] {
    for (const route of routes) {
      if (route.code === code) {
        results.push(route);
      }
      if (route.subRoutes) {
        this.findAllRoutesByCode(route.subRoutes, code, results);
      }
    }
    return results;
  }

  /**
   * Given multiple candidate routes, finds the one whose path best matches the URL
   */
  private findBestMatchingRoute(
    candidates: Route[],
    urlSegments: string[],
    preferredSidebarCode?: string,
  ): Route | null {
    let bestMatch: Route | null = null;
    let bestScore = -1;
    
    for (const candidate of candidates) {
      // Find the path to this candidate
      const routePath = this.findRoutePathToTarget(this.navigationRoutes, candidate);
      if (routePath) {
        // Calculate a matching score based on how many path segments match the URL
        const score = this.calculatePathMatchScore(routePath, urlSegments);
        const preferredBonus = preferredSidebarCode && this.routePathHasSidebar(routePath, preferredSidebarCode) ? 1000 : 0;
        if (score + preferredBonus > bestScore) {
          bestScore = score + preferredBonus;
          bestMatch = candidate;
        }
      }
    }
    
    return bestMatch;
  }

  /**
   * Calculates how well a route path matches the URL segments
   */
  private calculatePathMatchScore(routePath: Route[], urlSegments: string[]): number {
    let score = 0;
    
    for (const route of routePath) {
      if (route.path) {
        for (let i = 0; i < route.path.length && i < urlSegments.length; i++) {
          if (route.path[i] === urlSegments[i] || route.path[i].startsWith(':')) {
            score++;
          }
        }
      }
    }
    
    return score;
  }

  /**
   * Expands the sidebar based on the current URL by finding the matching route
   */
  private expandRouteByCurrentUrl(): void {
    if (!this.navigationRoutes) {
      return;
    }

    const currentUrl = this.router.url;
    const urlSegments = currentUrl.split('/').filter(segment => segment.length > 0);
    
    // Try to find a route that matches the current URL
    const matchingRoute = this.findMatchingRouteByUrl(this.navigationRoutes, urlSegments);
    
    if (matchingRoute) {
      const routePath = this.findRoutePathToTarget(this.navigationRoutes, matchingRoute);
      if (routePath) {
        this.openRoutePath(routePath);
      }
    }
  }

  /**
   * Recursively searches for a route whose path matches the URL segments
   */
  private findMatchingRouteByUrl(
    routes: Route[],
    urlSegments: string[],
    preferredSidebarCode?: string,
  ): Route | null {
    let fallback: Route | null = null;

    for (const route of routes) {
      if (route.path && this.routePathMatchesUrl(route.path, urlSegments)) {
        const routePath = this.findRoutePathToTarget(this.navigationRoutes, route);
        if (!preferredSidebarCode || this.routePathHasSidebar(routePath, preferredSidebarCode)) {
          return route;
        }
        if (!fallback) {
          fallback = route;
        }
      }

      if (route.subRoutes) {
        const found = this.findMatchingRouteByUrl(route.subRoutes, urlSegments, preferredSidebarCode);
        if (found) {
          const routePath = this.findRoutePathToTarget(this.navigationRoutes, found);
          if (!preferredSidebarCode || this.routePathHasSidebar(routePath, preferredSidebarCode)) {
            return found;
          }
          if (!fallback) {
            fallback = found;
          }
        }
      }
    }

    return fallback;
  }

  /**
   * Checks if a route's path matches the beginning of URL segments
   */
  private routePathMatchesUrl(routePath: string[], urlSegments: string[]): boolean {
    if (routePath.length > urlSegments.length) {
      return false;
    }
    
    for (let i = 0; i < routePath.length; i++) {
      // Skip dynamic segments (those starting with :)
      if (routePath[i].startsWith(':')) {
        continue;
      }
      
      if (routePath[i] !== urlSegments[i]) {
        return false;
      }
    }
    
    return true;
  }

  private isDescendant(root: Route | undefined, target: Route): boolean {
    if (!root?.subRoutes || root.subRoutes.length === 0) {
      return false;
    }
    for (const child of root.subRoutes) {
      if (child === target) {
        return true;
      }
      if (this.isDescendant(child, target)) {
        return true;
      }
    }
    return false;
  }

  private findParent(routes: Route[], target: Route): Route | null {
    for (const route of routes) {
      if (route.subRoutes?.includes(target)) {
        return route;
      }
      if (route.subRoutes) {
        const parent = this.findParent(route.subRoutes, target);
        if (parent) {
          return parent;
        }
      }
    }
    return null;
  }

  private closeSiblings(parent: Route | null, target: Route, routes: Route[]) {
    const siblings = parent ? parent.subRoutes : routes;
    if (siblings) {
      for (const sibling of siblings) {
        if (sibling !== target && sibling.opened) {
          sibling.opened = false;
        }
      }
    }
  }

  changeNavigation(route: Route) {
    if (route.subRoutes && route.subRoutes.length > 0) {
      // If the route has subroutes, toggle its opened state
      // Close siblings at the same level, but keep parent routes open
      const parent = this.findParent(this.navigationRoutes, route);
      this.closeSiblings(parent, route, this.navigationRoutes);
      
      // Toggle the clicked route
      route.opened = !route.opened;
      
      // If we're closing a route, also close all its children
      if (!route.opened) {
        this.closeAllRoutes(route.subRoutes);
      }
    } else {
      // If the route has no subroutes, navigate to it
      // Keep parent routes open (they will be maintained by setNavigationOpenOnChange)
      if (route.sidebarOverride && route.path) {
        const urlTree = this.router.createUrlTree(route.path as any[]);
        const serializedUrl = this.router.serializeUrl(urlTree);
        this.pendingSidebarOverride = {
          code: route.sidebarOverride,
          url: serializedUrl.startsWith('/') ? serializedUrl : `/${serializedUrl}`,
        };
      } else {
        this.pendingSidebarOverride = null;
      }
      this.router.navigate(route.path as any[]);
    }
  }

  private routePathHasSidebar(routePath: Route[] | null | undefined, sidebarCode?: string): boolean {
    if (!routePath || !sidebarCode) {
      return !sidebarCode;
    }
    return routePath.some((segment) => segment.code === sidebarCode);
  }
}

